import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { useAppConfig } from '@pickleballinc/ui/components/global/ApplicationConfigProvider';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useImpersonationBarHeight } from '@/hooks/useImpersonationBarHeight';
import { formatNameWithInitial } from '@/modules/results/helpers';
import { calculateStatistics } from '@/modules/results/match/helperMatchStats';
import InfoModal from '@/modules/results/match/InfoModal';
import MatchCardWithPlayers from '@/modules/results/match/ListComponents/MatchCardWithPlayers';
import MatchDuration from '@/modules/results/match/ListComponents/MatchDuration';
import MatchGamePoints from '@/modules/results/match/ListComponents/MatchGamePoints';
import MatchListCard from '@/modules/results/match/ListComponents/MatchListCard';
import MatchListCardWithGames from '@/modules/results/match/ListComponents/MatchListCardWithGames';
import MatchTimeouts from '@/modules/results/match/ListComponents/MatchTimeouts';
import { GameStats } from '@/modules/results/types';

interface Player {
  name: string;
  uuid: string;
  teamUuid: string;
}

interface MatchStatsProps {
  matchStats: GameStats;
  playerList: Player[];
}

interface TimeoutUsedEntity {
  TimeoutStarted?: string;
  TeamOneScore?: number;
  TeamTwoScore?: number;
  TimeoutCalledByUuid?: string;
  ServerWhenTimeoutCalledFullName?: string;
  NextRallyResult?: string;
  start?: string;
}

export const MatchStats = ({ matchStats, playerList }: MatchStatsProps) => {
  const [activeTab, setActiveTab] = useState<string>('Match');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeoutsData, setTimeoutsData] = useState<TimeoutUsedEntity | null>(null);
  const [showAll, setShowAll] = useState(false);
  const toggleButtonRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isSingle = playerList.length > 2 ? false : true;

  const {
    config: { isImpersonationInProcess },
  } = useAppConfig();

  const barHeight = useImpersonationBarHeight();

  const noStreakText = isSingle ? 'player' : 'team';

  const statistics = useMemo(() => {
    return calculateStatistics(matchStats);
  }, [matchStats]);

  const teamOnePlayerOneName = formatNameWithInitial(
    matchStats?.MatchInfo?.team_one_player_one_name
  );
  const teamOnePlayerTwoName = formatNameWithInitial(
    matchStats?.MatchInfo?.team_one_player_two_name
  );
  const teamTwoPlayerOneName = formatNameWithInitial(
    matchStats?.MatchInfo?.team_two_player_one_name
  );
  const teamTwoPlayerTwoName = formatNameWithInitial(
    matchStats?.MatchInfo?.team_two_player_two_name
  );

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleTimeoutsModal = (itemData: TimeoutUsedEntity) => {
    const utc = matchStats?.MatchInfo?.utc_offset;
    const abbreviation = matchStats?.MatchInfo?.timezone_abbreviation;

    matchStats?.TeamOneMatchStats?.TimeoutUsed?.forEach((it) => {
      const time = formatDateTime(it.TimeoutStarted, {
        utcOffset: utc,
        timezoneAbbreviation: abbreviation,
        type: 'time',
        precision: 'sec',
      });
      if (itemData.start === time) {
        setTimeoutsData(it);
      }
    });
    matchStats?.TeamTwoMatchStats?.TimeoutUsed?.forEach((it) => {
      const time = formatDateTime(it.TimeoutStarted, {
        utcOffset: utc,
        timezoneAbbreviation: abbreviation,
        type: 'time',
        precision: 'sec',
      });
      if (itemData.start === time) {
        setTimeoutsData(it);
      }
    });

    setIsModalOpen(true);
  };

  const showAllHandler = () => {
    setShowAll(!showAll);
    toggleButtonRef.current?.scrollIntoView();
  };

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 600) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  return (
    <div className='w-full'>
      <ul className='flex w-[340px] flex-nowrap justify-start gap-3 overflow-x-auto px-2 pb-3 sm:w-full sm:justify-center'>
        {statistics && Object.keys(statistics).length > 1
          ? Object.keys(statistics).map((tab) => (
              <li key={tab}>
                <button
                  className={clsx(
                    'min-w-[80px] cursor-pointer rounded-md border border-gray-300 p-2 text-center md:min-w-[160px]',
                    activeTab === tab ? 'bg-experiment text-white' : 'bg-white text-black'
                  )}
                  onClick={() => handleTabClick(tab)}
                >
                  {tab}
                </button>
              </li>
            ))
          : null}
      </ul>
      {statistics && statistics[activeTab] ? (
        <div className='mt-4 w-full rounded-lg border bg-white'>
          <div>
            <div className='w-full text-center sm:m-auto sm:w-2/3'>
              <div
                style={isImpersonationInProcess ? { top: `${barHeight}px` } : { top: '0' }}
                className={clsx(
                  'z-10 bg-black text-sm text-white lg:text-base',
                  isVisible ? 'sticky' : 'hidden'
                )}
              >
                <div className='flex min-h-[40px] items-center justify-center lg:py-3'>
                  <div className='flex flex-1 flex-col items-center justify-center lg:flex-row lg:gap-2'>
                    <span>{teamOnePlayerOneName}</span>
                    {teamOnePlayerTwoName !== '' && (
                      <>
                        <span className='hidden lg:flex'>&</span>
                        <span>{teamOnePlayerTwoName}</span>
                      </>
                    )}
                  </div>
                  <div className='flex-1'>
                    {matchStats.MatchInfo.format_title === 'Doubles' ? 'Teams' : 'Players'}
                  </div>
                  <div className='flex flex-1 flex-col items-center justify-center lg:flex-row lg:gap-2'>
                    <span>{teamTwoPlayerOneName}</span>
                    {teamTwoPlayerTwoName !== '' && (
                      <>
                        <span className='hidden lg:flex'>&</span>
                        <span>{teamTwoPlayerTwoName}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <MatchDuration
                date={formatDateTime(matchStats.MatchInfo.match_start, {
                  includeWeekday: true,
                })}
                value={statistics[activeTab].duration.formattedDuration}
                start={statistics[activeTab].duration.formattedStartTime}
                end={statistics[activeTab].duration.formattedEndTime}
                info={matchStats.MatchInfo}
                text='Duration'
                activeTab={activeTab}
              />
              <MatchListCard
                value1={statistics[activeTab].totalPointsWon.teamOne}
                value2={statistics[activeTab].totalPointsWon.teamTwo}
                text1='Total Points Won'
              />
              <MatchTimeouts
                value1={statistics[activeTab].timeoutsUsed.teamOne.number}
                value2={statistics[activeTab].timeoutsUsed.teamTwo.number}
                text='Timeouts Used'
                timeouts1={statistics[activeTab].timeoutsUsed.teamOne.timeouts}
                timeouts2={statistics[activeTab].timeoutsUsed.teamTwo.timeouts}
                handleTimeoutsModal={handleTimeoutsModal}
              />
              <MatchGamePoints
                value1={statistics[activeTab].gamePoints.teamOne?.length || 0}
                value2={statistics[activeTab].gamePoints.teamTwo?.length || 0}
                text='Game Points'
                text2='Number of times the team had the game winning serve'
                points1={statistics[activeTab].gamePoints.teamOne || []}
                points2={statistics[activeTab].gamePoints.teamTwo || []}
              />
              <MatchListCardWithGames
                start1={statistics[activeTab].longestPointStreak.teamOne.start}
                start2={statistics[activeTab].longestPointStreak.teamTwo.start}
                end1={statistics[activeTab].longestPointStreak.teamOne.end}
                end2={statistics[activeTab].longestPointStreak.teamTwo.end}
                streak1={statistics[activeTab].longestPointStreak.teamOne.streak}
                streak2={statistics[activeTab].longestPointStreak.teamTwo.streak}
                text1='Longest Point Streak'
                text2='This is the number of points earned in a row during ball possession'
                games1={statistics[activeTab].longestPointStreak.teamOne.games}
                games2={statistics[activeTab].longestPointStreak.teamTwo.games}
              />
              <div ref={toggleButtonRef}>
                <MatchListCardWithGames
                  start1={statistics[activeTab].noPointStreak.teamOne.start}
                  start2={statistics[activeTab].noPointStreak.teamTwo.start}
                  end1={statistics[activeTab].noPointStreak.teamOne.end}
                  end2={statistics[activeTab].noPointStreak.teamTwo.end}
                  streak1={statistics[activeTab].noPointStreak.teamOne.streak}
                  streak2={statistics[activeTab].noPointStreak.teamTwo.streak}
                  text1='No Point Streak'
                  text2={`This is the number of side outs without winning a point by the ${noStreakText}`}
                  games1={statistics[activeTab].noPointStreak.teamOne.games}
                  games2={statistics[activeTab].noPointStreak.teamTwo.games}
                />
              </div>
              {showAll ? (
                <div>
                  <MatchListCard
                    value1={statistics[activeTab].sideOuts.teamOne.sideOuts}
                    value2={statistics[activeTab].sideOuts.teamTwo.sideOuts}
                    text1='Total Side Outs'
                  />
                  <MatchCardWithPlayers
                    serves1={statistics[activeTab].serves.teamTwo.serves}
                    serves2={statistics[activeTab].serves.teamOne.serves}
                    text1='Total Serves'
                    text2=''
                    serves1PlayerOne={statistics[activeTab].serves.teamOne.servesPlayerOne}
                    serves1PlayerTwo={statistics[activeTab].serves.teamOne.servesPlayerTwo}
                    serves2PlayerOne={statistics[activeTab].serves.teamTwo.servesPlayerOne}
                    serves2PlayerTwo={statistics[activeTab].serves.teamTwo.servesPlayerTwo}
                    teamOnePlayerOneName={teamOnePlayerOneName}
                    teamOnePlayerTwoName={teamOnePlayerTwoName}
                    teamTwoPlayerOneName={teamTwoPlayerOneName}
                    teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                  />
                  {matchStats.MatchInfo.format_title === 'Doubles' ? (
                    <div>
                      <MatchCardWithPlayers
                        serves1={statistics[activeTab].firstServes.teamOne.firstServes}
                        serves2={statistics[activeTab].firstServes.teamTwo.firstServes}
                        text1='Total First Server Point'
                        text2='total number of point from the first server after a side out'
                        serves1PlayerOne={
                          statistics[activeTab].firstServes.teamOne.firstServesPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].firstServes.teamOne.firstServesPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].firstServes.teamTwo.firstServesPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].firstServes.teamTwo.firstServesPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={statistics[activeTab].secondServes.teamOne.secondServes}
                        serves2={statistics[activeTab].secondServes.teamTwo.secondServes}
                        text1='Total Second Server Point'
                        text2='total number of point from the second server after a side out'
                        serves1PlayerOne={
                          statistics[activeTab].secondServes.teamOne.secondServesPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].secondServes.teamOne.secondServesPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].secondServes.teamTwo.secondServesPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].secondServes.teamTwo.secondServesPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={
                          statistics[activeTab].winRalliesServing.teamOne
                            .winRalliesWhenInServingPosition
                        }
                        serves2={
                          statistics[activeTab].winRalliesServing.teamTwo
                            .winRalliesWhenInServingPosition
                        }
                        text1='Total Win Rallies When In Serving Position'
                        text2='total rallies won when serving'
                        serves1PlayerOne={
                          statistics[activeTab].winRalliesServing.teamOne
                            .winRalliesWhenInServingPositionPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].winRalliesServing.teamOne
                            .winRalliesWhenInServingPositionPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].winRalliesServing.teamTwo
                            .winRalliesWhenInServingPositionPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].winRalliesServing.teamTwo
                            .winRalliesWhenInServingPositionPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={
                          statistics[activeTab].lossRalliesServing.teamOne
                            .lossRalliesWhenInServingPosition
                        }
                        serves2={
                          statistics[activeTab].lossRalliesServing.teamTwo
                            .lossRalliesWhenInServingPosition
                        }
                        text1='Total Loss Rallies When In Serving Position'
                        text2='total rallies loss when serving'
                        serves1PlayerOne={
                          statistics[activeTab].lossRalliesServing.teamOne
                            .lossRalliesWhenInServingPositionPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].lossRalliesServing.teamOne
                            .lossRalliesWhenInServingPositionPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].lossRalliesServing.teamTwo
                            .lossRalliesWhenInServingPositionPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].lossRalliesServing.teamTwo
                            .lossRalliesWhenInServingPositionPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={statistics[activeTab].serves.teamOne.serves}
                        serves2={statistics[activeTab].serves.teamTwo.serves}
                        text1='Total Received Serves'
                        text2=''
                        serves1PlayerOne={statistics[activeTab].serves.teamTwo.servesPlayerOne}
                        serves1PlayerTwo={statistics[activeTab].serves.teamTwo.servesPlayerTwo}
                        serves2PlayerOne={statistics[activeTab].serves.teamOne.servesPlayerOne}
                        serves2PlayerTwo={statistics[activeTab].serves.teamOne.servesPlayerTwo}
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={
                          statistics[activeTab].winReceivingServing.teamOne.winReceivingServing
                        }
                        serves2={
                          statistics[activeTab].winReceivingServing.teamTwo.winReceivingServing
                        }
                        text1='Total Win Rallies When Receiving Serve'
                        text2='Total rallies won when receiving serve'
                        serves1PlayerOne={
                          statistics[activeTab].winReceivingServing.teamOne
                            .winReceivingServingPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].winReceivingServing.teamOne
                            .winReceivingServingPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].winReceivingServing.teamTwo
                            .winReceivingServingPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].winReceivingServing.teamTwo
                            .winReceivingServingPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                      <MatchCardWithPlayers
                        serves1={
                          statistics[activeTab].lossReceivingServing.teamOne.lossReceivingServing
                        }
                        serves2={
                          statistics[activeTab].lossReceivingServing.teamTwo.lossReceivingServing
                        }
                        text1='Total Loss Rallies When Receiving Serve'
                        text2='Total rallies loss when receiving serve'
                        serves1PlayerOne={
                          statistics[activeTab].lossReceivingServing.teamOne
                            .lossReceivingServingPlayerOne
                        }
                        serves1PlayerTwo={
                          statistics[activeTab].lossReceivingServing.teamOne
                            .lossReceivingServingPlayerTwo
                        }
                        serves2PlayerOne={
                          statistics[activeTab].lossReceivingServing.teamTwo
                            .lossReceivingServingPlayerOne
                        }
                        serves2PlayerTwo={
                          statistics[activeTab].lossReceivingServing.teamTwo
                            .lossReceivingServingPlayerTwo
                        }
                        teamOnePlayerOneName={teamOnePlayerOneName}
                        teamOnePlayerTwoName={teamOnePlayerTwoName}
                        teamTwoPlayerOneName={teamTwoPlayerOneName}
                        teamTwoPlayerTwoName={teamTwoPlayerTwoName}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className='flex items-center justify-center py-2'>
              <div onClick={showAllHandler} className='cursor-pointer p-4'>
                {showAll ? 'Show less' : 'See all stats'}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={timeoutsData}
        playerList={playerList}
      />
    </div>
  );
};
